/**
 * Supplier Balances (AP Statement) report.
 *
 * Shows each supplier's opening balance, PO total within the selected period,
 * payments made within the period, and resulting closing balance. Clicking a
 * row opens a side sheet with the supplier's payment history and outstanding
 * POs. Includes a KPI strip and export (CSV + print).
 */

import { useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  ArrowUpRight,
  Receipt,
  Users,
} from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import { formatDate, formatRelative } from "@bizop/shared/dates";
import {
  Badge,
  Button,
  DataTable,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  cn,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  mockSupplierPayments,
  mockSupplierInvoices,
  type MockSupplierPayment,
  type MockSupplierInvoice,
} from "../../../lib/payablesMock";
import {
  mockPurchaseOrders,
  mockSuppliers,
} from "../../../components/imports/importsMockData";
import {
  PeriodSelector,
  defaultPeriodWindow,
  type PeriodWindow,
} from "../../../components/gl/PeriodSelector";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  printElement,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";

export const Route = createFileRoute("/_app/reports/supplier-balances")({
  component: SupplierBalancesPage,
});

/* ─────────────────────────────────────────────────────────────
 * Data types
 * ───────────────────────────────────────────────────────────── */

type SupplierBalanceRow = {
  supplierId: string;
  supplierCode: string;
  supplierName: string;
  /** Balance before the period: sum of PO totals before period start minus
   *  payments before period start. */
  openingBalanceCents: bigint;
  /** Sum of PO totals for POs ordered within the period. */
  poTotalCents: bigint;
  /** Sum of payments made within the period. */
  paymentsCents: bigint;
  /** openingBalance + poTotal - payments */
  closingBalanceCents: bigint;
  /** True if any PO's due date has passed and closing balance > 0. */
  isPastDue: boolean;
};

/* ─────────────────────────────────────────────────────────────
 * Computation helpers
 * ───────────────────────────────────────────────────────────── */

const NOW = new Date("2026-05-09T08:00:00Z").getTime();

function buildSupplierBalances(
  fromDate: number,
  toDate: number,
): SupplierBalanceRow[] {
  // Group POs by supplier
  const posBySupplier = new Map<string, typeof mockPurchaseOrders>();
  for (const po of mockPurchaseOrders) {
    if (po.status === "cancelled") continue;
    const list = posBySupplier.get(po.supplierId) ?? [];
    list.push(po);
    posBySupplier.set(po.supplierId, list);
  }

  // Group supplier payments by supplier
  const paymentsBySupplier = new Map<string, MockSupplierPayment[]>();
  for (const p of mockSupplierPayments) {
    const list = paymentsBySupplier.get(p.supplierId) ?? [];
    list.push(p);
    paymentsBySupplier.set(p.supplierId, list);
  }

  const rows: SupplierBalanceRow[] = [];

  for (const supplier of mockSuppliers) {
    const supplierId = supplier._id;
    const pos = posBySupplier.get(supplierId) ?? [];
    const payments = paymentsBySupplier.get(supplierId) ?? [];

    // Opening balance = PO totals placed BEFORE the period - payments made BEFORE the period
    const openingPoTotal = pos
      .filter((po) => po.orderDate < fromDate)
      .reduce((s, po) => s + po.totalAmountInBaseCents, 0n);
    const openingPayments = payments
      .filter((p) => p.occurredAt < fromDate)
      .reduce((s, p) => s + p.amountInBaseCents, 0n);
    const openingBalanceCents = openingPoTotal - openingPayments < 0n
      ? 0n
      : openingPoTotal - openingPayments;

    // Period PO total = POs ordered within [fromDate, toDate]
    const poTotalCents = pos
      .filter((po) => po.orderDate >= fromDate && po.orderDate <= toDate)
      .reduce((s, po) => s + po.totalAmountInBaseCents, 0n);

    // Period payments = payments made within [fromDate, toDate]
    const paymentsCents = payments
      .filter((p) => p.occurredAt >= fromDate && p.occurredAt <= toDate)
      .reduce((s, p) => s + p.amountInBaseCents, 0n);

    const closingRaw = openingBalanceCents + poTotalCents - paymentsCents;
    const closingBalanceCents = closingRaw < 0n ? 0n : closingRaw;

    // Past due = has a PO with expectedDeliveryDate in the past and still owes money
    const isPastDue =
      closingBalanceCents > 0n &&
      pos.some(
        (po) =>
          po.expectedDeliveryDate !== null &&
          po.expectedDeliveryDate !== undefined &&
          po.expectedDeliveryDate < NOW,
      );

    rows.push({
      supplierId,
      supplierCode: supplier.code,
      supplierName: supplier.displayName,
      openingBalanceCents,
      poTotalCents,
      paymentsCents,
      closingBalanceCents,
      isPastDue,
    });
  }

  // Sort by closing balance desc
  rows.sort((a, b) => {
    if (a.closingBalanceCents > b.closingBalanceCents) return -1;
    if (a.closingBalanceCents < b.closingBalanceCents) return 1;
    return 0;
  });

  return rows;
}

/* ─────────────────────────────────────────────────────────────
 * Page component
 * ───────────────────────────────────────────────────────────── */

function SupplierBalancesPage() {
  const isMock = useMockData();
  void isMock;

  const [period, setPeriod] = useState<PeriodWindow>(defaultPeriodWindow());
  const [activeSupplier, setActiveSupplier] = useState<SupplierBalanceRow | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const rows = useMemo(
    () => buildSupplierBalances(period.fromDate, period.toDate),
    [period.fromDate, period.toDate],
  );

  /* ── KPIs ────────────────────────────────────────────────── */
  const totalSuppliers = rows.length;
  const openBalance = rows.reduce((s, r) => s + r.closingBalanceCents, 0n);
  const largestBalance = rows.reduce(
    (max, r) => (r.closingBalanceCents > max ? r.closingBalanceCents : max),
    0n,
  );
  const pastDueCount = rows.filter((r) => r.isPastDue).length;

  /* ── Footer sum ──────────────────────────────────────────── */
  const footerOpeningTotal = rows.reduce((s, r) => s + r.openingBalanceCents, 0n);
  const footerPoTotal = rows.reduce((s, r) => s + r.poTotalCents, 0n);
  const footerPaymentsTotal = rows.reduce((s, r) => s + r.paymentsCents, 0n);
  const footerClosingTotal = rows.reduce((s, r) => s + r.closingBalanceCents, 0n);

  /* ── CSV export ──────────────────────────────────────────── */
  function handleCsv(extension: "csv" | "xls") {
    const columns: ExportColumn<SupplierBalanceRow>[] = [
      { key: "supplierCode", label: "Supplier Code" },
      { key: "supplierName", label: "Supplier Name" },
      { key: "openingBalanceCents", label: "Opening Balance (BDT)" },
      { key: "poTotalCents", label: "PO Total (BDT)" },
      { key: "paymentsCents", label: "Payments (BDT)" },
      { key: "closingBalanceCents", label: "Closing Balance (BDT)" },
      {
        key: "isPastDue",
        label: "Status",
        format: (v) => (v ? "Past Due" : "Current"),
      },
    ];
    downloadCsv(
      exportFilename("supplier-balances", extension),
      toCsv(rows, columns),
    );
  }

  function handlePrint() {
    printElement(printRef.current, {
      title: "Supplier Balances (AP Statement)",
      subtitle: `${formatDate(period.fromDate, { style: "short" })} → ${formatDate(period.toDate, { style: "short" })}`,
    });
  }

  /* ── Table columns ───────────────────────────────────────── */
  const columns = useMemo<ColumnDef<SupplierBalanceRow>[]>(
    () => [
      {
        id: "code",
        header: "Supplier Code",
        cell: ({ row }) => (
          <span className="font-mono text-[12px] text-(--text-secondary)">
            {row.original.supplierCode}
          </span>
        ),
        meta: { mono: true },
      },
      {
        id: "name",
        header: "Supplier Name",
        cell: ({ row }) => (
          <span className="text-(--text-primary) font-medium">
            {row.original.supplierName}
          </span>
        ),
      },
      {
        id: "opening",
        header: "Opening Balance",
        meta: { numeric: true, mono: true },
        cell: ({ row }) => (
          <span className="font-mono tabular-nums text-(--text-secondary)">
            {row.original.openingBalanceCents === 0n
              ? <span className="text-(--text-tertiary)">—</span>
              : formatMoney(row.original.openingBalanceCents, "BDT", {
                  locale: "en-BD",
                })}
          </span>
        ),
      },
      {
        id: "poTotal",
        header: "PO Total (period)",
        meta: { numeric: true, mono: true },
        cell: ({ row }) => (
          <span className="font-mono tabular-nums text-(--text-secondary)">
            {row.original.poTotalCents === 0n
              ? <span className="text-(--text-tertiary)">—</span>
              : formatMoney(row.original.poTotalCents, "BDT", {
                  locale: "en-BD",
                })}
          </span>
        ),
      },
      {
        id: "payments",
        header: "Payments (period)",
        meta: { numeric: true, mono: true },
        cell: ({ row }) => (
          <span className="font-mono tabular-nums text-(--success)">
            {row.original.paymentsCents === 0n
              ? <span className="text-(--text-tertiary)">—</span>
              : formatMoney(row.original.paymentsCents, "BDT", {
                  locale: "en-BD",
                })}
          </span>
        ),
      },
      {
        id: "closing",
        header: "Closing Balance",
        meta: { numeric: true, mono: true },
        cell: ({ row }) => (
          <span
            className={cn(
              "font-mono tabular-nums font-semibold",
              row.original.closingBalanceCents === 0n
                ? "text-(--text-tertiary)"
                : "text-(--text-primary)",
            )}
          >
            {formatMoney(row.original.closingBalanceCents, "BDT", {
              locale: "en-BD",
            })}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) =>
          row.original.closingBalanceCents === 0n ? (
            <Badge variant="success" size="sm">Settled</Badge>
          ) : row.original.isPastDue ? (
            <Badge variant="danger" size="sm">Past Due</Badge>
          ) : (
            <Badge variant="info" size="sm">Current</Badge>
          ),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-6" ref={printRef}>
      <PageHeader
        title="Supplier Balances"
        description={`AP statement for ${formatDate(period.fromDate, { style: "short" })} → ${formatDate(period.toDate, { style: "short" })}.`}
        actions={
          <ExportButton
            onCsv={() => handleCsv("csv")}
            onExcel={() => handleCsv("xls")}
            onPrint={handlePrint}
          />
        }
      />

      <PeriodSelector value={period} onChange={setPeriod} />

      {/* KPI strip */}
      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        aria-label="Supplier balance KPIs"
      >
        <KpiCard
          caption="Total Suppliers"
          value={totalSuppliers}
          hint="Active in ledger"
        />
        <KpiCard
          caption="Open Balance (BDT)"
          value={formatMoneySmart(openBalance, "BDT", { locale: "en-BD" })}
          hint="Sum of closing balances"
        />
        <KpiCard
          caption="Largest Single Balance"
          value={formatMoneySmart(largestBalance, "BDT", { locale: "en-BD" })}
          hint="Highest closing balance"
        />
        <KpiCard
          caption="Suppliers Past Due"
          value={pastDueCount}
          hint={
            pastDueCount > 0
              ? "Have overdue POs with open balance"
              : "All current"
          }
        />
      </section>

      {/* Main table */}
      <DataTable
        data={rows}
        columns={columns}
        getRowId={(r) => r.supplierId}
        onRowClick={(r) => setActiveSupplier(r.original)}
        emptyTitle="No supplier balances"
        emptyDescription="No purchase orders or payments in this period."
        emptyIcon={<Receipt className="h-5 w-5" aria-hidden />}
      />

      {/* Footer summary */}
      <div className="rounded-md border border-(--border-subtle) bg-(--bg-surface) p-3 text-[13px]">
        <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] gap-2 font-mono tabular-nums">
          <div className="font-semibold text-(--text-primary)">
            Total ({rows.length} supplier{rows.length === 1 ? "" : "s"})
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase text-(--text-tertiary) mb-0.5">Opening</div>
            <div className="text-(--text-primary)">
              {formatMoney(footerOpeningTotal, "BDT", { locale: "en-BD" })}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase text-(--text-tertiary) mb-0.5">PO Total</div>
            <div className="text-(--text-secondary)">
              {formatMoney(footerPoTotal, "BDT", { locale: "en-BD" })}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase text-(--text-tertiary) mb-0.5">Payments</div>
            <div className="text-(--success)">
              {formatMoney(footerPaymentsTotal, "BDT", { locale: "en-BD" })}
            </div>
          </div>
          <div className="text-right col-span-2">
            <div className="text-[10px] uppercase text-(--text-tertiary) mb-0.5">Closing</div>
            <div className="text-(--text-primary) font-semibold">
              {formatMoney(footerClosingTotal, "BDT", { locale: "en-BD" })}
            </div>
          </div>
        </div>
      </div>

      {/* Detail sheet */}
      <Sheet
        open={activeSupplier !== null}
        onOpenChange={(open) => {
          if (!open) setActiveSupplier(null);
        }}
      >
        <SheetContent side="right" size="md">
          {activeSupplier ? (
            <SupplierDetailSheet
              row={activeSupplier}
              onClose={() => setActiveSupplier(null)}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Supplier detail sheet
 * ───────────────────────────────────────────────────────────── */

function SupplierDetailSheet({
  row,
  onClose,
}: {
  row: SupplierBalanceRow;
  onClose: () => void;
}) {
  const payments = useMemo(
    () =>
      mockSupplierPayments
        .filter((p) => p.supplierId === row.supplierId)
        .sort((a, b) => b.occurredAt - a.occurredAt),
    [row.supplierId],
  );

  const outstandingPos = useMemo(
    () =>
      mockPurchaseOrders
        .filter(
          (po) =>
            po.supplierId === row.supplierId && po.status !== "cancelled",
        )
        .sort((a, b) => b.orderDate - a.orderDate),
    [row.supplierId],
  );

  const invoicesByPo = useMemo(() => {
    const map = new Map<string, MockSupplierInvoice[]>();
    for (const inv of mockSupplierInvoices) {
      if (inv.supplierId !== row.supplierId) continue;
      const list = map.get(inv.purchaseOrderId) ?? [];
      list.push(inv);
      map.set(inv.purchaseOrderId, list);
    }
    return map;
  }, [row.supplierId]);

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-(--accent-soft) text-(--accent)">
            <Users className="h-4 w-4" />
          </span>
          {row.supplierName}
        </SheetTitle>
        <SheetDescription>
          {row.supplierCode} ·{" "}
          {row.isPastDue ? (
            <span className="text-(--danger)">Past Due</span>
          ) : (
            "Current"
          )}
        </SheetDescription>
      </SheetHeader>

      <SheetBody className="flex flex-col gap-5">
        {/* Balance summary */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-md bg-(--bg-canvas) border border-(--border-subtle) px-3 py-3">
            <div className="text-[11px] text-(--text-tertiary) uppercase tracking-wide mb-1">
              Opening Balance
            </div>
            <div className="font-mono tabular-nums text-[14px] font-semibold text-(--text-secondary)">
              {formatMoneySmart(row.openingBalanceCents, "BDT", {
                locale: "en-BD",
              })}
            </div>
          </div>
          <div className="rounded-md bg-(--bg-canvas) border border-(--border-subtle) px-3 py-3">
            <div className="text-[11px] text-(--text-tertiary) uppercase tracking-wide mb-1">
              Closing Balance
            </div>
            <div className="font-mono tabular-nums text-[14px] font-semibold text-(--text-primary)">
              {formatMoneySmart(row.closingBalanceCents, "BDT", {
                locale: "en-BD",
              })}
            </div>
          </div>
        </div>

        {/* Payment history */}
        <div className="flex flex-col gap-2">
          <h3 className="text-[13px] font-semibold text-(--text-primary)">
            Payment History
          </h3>
          {payments.length === 0 ? (
            <div className="rounded-md border border-dashed border-(--border-subtle) bg-(--bg-canvas) px-4 py-6 text-center text-[12px] text-(--text-tertiary)">
              No payments recorded
            </div>
          ) : (
            <ul className="rounded-md border border-(--border-subtle) bg-(--bg-surface) overflow-hidden divide-y divide-[var(--border-subtle)]">
              {payments.map((p) => (
                <li
                  key={p._id}
                  className="grid grid-cols-[1fr_auto] items-center gap-3 px-3 py-2"
                >
                  <div className="flex flex-col min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-[12px] text-(--text-primary) font-medium font-mono">
                        {p.paymentNumber}
                      </span>
                      <span className="text-[11px] text-(--text-tertiary)">
                        {formatRelative(p.occurredAt)}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2 text-[11px] text-(--text-tertiary)">
                      <span className="capitalize">
                        {p.paymentMethod.replace(/_/g, " ")}
                      </span>
                      {p.reference ? (
                        <>
                          <span aria-hidden>·</span>
                          <span className="italic truncate">{p.reference}</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono tabular-nums text-[12px] text-(--text-primary)">
                      {formatMoney(p.amountInBaseCents, "BDT", {
                        locale: "en-BD",
                      })}
                    </div>
                    {p.currency !== "BDT" ? (
                      <div className="text-[10px] text-(--text-tertiary) tabular-nums">
                        {formatMoney(p.amountCents, p.currency, {
                          display: "code",
                          locale: "en-BD",
                        })}
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Outstanding POs */}
        <div className="flex flex-col gap-2">
          <h3 className="text-[13px] font-semibold text-(--text-primary)">
            Purchase Orders
          </h3>
          {outstandingPos.length === 0 ? (
            <div className="rounded-md border border-dashed border-(--border-subtle) bg-(--bg-canvas) px-4 py-6 text-center text-[12px] text-(--text-tertiary)">
              No purchase orders
            </div>
          ) : (
            <ul className="rounded-md border border-(--border-subtle) bg-(--bg-surface) overflow-hidden divide-y divide-[var(--border-subtle)]">
              {outstandingPos.map((po) => {
                const invoices = invoicesByPo.get(po._id) ?? [];
                const invoicedTotal = invoices.reduce(
                  (s, inv) => s + inv.amountInBaseCents,
                  0n,
                );
                const poPayments = mockSupplierPayments
                  .filter((p) => p.purchaseOrderId === po._id)
                  .reduce((s, p) => s + p.amountInBaseCents, 0n);
                const outstanding = invoicedTotal > 0n
                  ? invoicedTotal - poPayments
                  : po.totalAmountInBaseCents - poPayments;
                const outstandingClamped = outstanding < 0n ? 0n : outstanding;

                return (
                  <li
                    key={po._id}
                    className="grid grid-cols-[1fr_auto] items-center gap-3 px-3 py-2"
                  >
                    <div className="flex flex-col min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-[12px] text-(--text-primary) font-medium font-mono">
                          {po.poNumber}
                        </span>
                        <span className="text-[11px] text-(--text-tertiary)">
                          {formatDate(po.orderDate, { style: "short" })}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2 text-[11px] text-(--text-tertiary)">
                        <span className="capitalize">{po.status.replace(/_/g, " ")}</span>
                        {po.expectedDeliveryDate ? (
                          <>
                            <span aria-hidden>·</span>
                            <span>
                              Due{" "}
                              {formatDate(po.expectedDeliveryDate, {
                                style: "short",
                              })}
                            </span>
                          </>
                        ) : null}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono tabular-nums text-[12px] text-(--text-primary)">
                        {outstandingClamped === 0n ? (
                          <span className="text-(--success)">Settled</span>
                        ) : (
                          formatMoney(outstandingClamped, "BDT", {
                            locale: "en-BD",
                          })
                        )}
                      </div>
                      <div className="text-[10px] text-(--text-tertiary) tabular-nums">
                        of{" "}
                        {formatMoney(po.totalAmountInBaseCents, "BDT", {
                          locale: "en-BD",
                        })}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Link to PO page */}
        <button
          type="button"
          className="flex items-center gap-1 text-[12px] text-(--accent) hover:underline underline-offset-2 w-fit"
          onClick={onClose}
        >
          <ArrowUpRight className="size-3" aria-hidden />
          View Purchase Orders
        </button>
      </SheetBody>

      <SheetFooter>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </SheetFooter>
    </>
  );
}
